import UpgradeAppContent from '../../../../BottomSheetComponents/UpgradeAppContent'
import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../types/misc.types'
import { postCTA, trackEvent } from '../../../../../utils/appBridge'
import { getSessionStorageItem } from '../../../../../utils/sessionStorage'
import { useEliteBuyCtaExtras } from '../../../EliteBuyCtaExtrasContext'

const OFFER_STRIP_BY_PRICE = {
  499: {
    src: '/images/elite/elite-join-offer-strip-499.png',
    alt: 'Join Elite at ₹499 per year plus GST. Less than ₹1.5 per day. Lowest price in the market. Available for a limited time. Was ₹1,999 per year.'
  },
  749: {
    src: '/images/elite/elite-join-offer-strip-749.png',
    alt: 'Join Elite at ₹749 per year plus GST. Less than ₹3 per day. Lowest price in the market. Available for a limited time. Was ₹1,999 per year.'
  },
  999: {
    src: '/images/elite/elite-join-offer-strip-999.png',
    alt: 'Join Elite at ₹999 per year plus GST. Less than ₹4 per day. Lowest price in the market. Available for a limited time. Was ₹1,999 per year.'
  },
  1249: {
    src: '/images/elite/elite-join-offer-strip-1249.png',
    alt: 'Join Elite at ₹1,249 per year plus GST. Less than ₹4 per day. Lowest price in the market. Available for a limited time. Was ₹1,999 per year.'
  },
  1499: {
    src: '/images/elite/elite-join-offer-strip-1499.png',
    alt: 'Join Elite at ₹1,499 per year plus GST. Less than ₹5 per day. Lowest price in the market. Available for a limited time. Was ₹1,999 per year.'
  }
} as const

export type EliteJoinOfferPriceLabel = keyof typeof OFFER_STRIP_BY_PRICE

type EliteLandingJoinOfferStripProps = {
  /** Offer price key that selects the strip asset (499 | 749 | 999 | 1249 | 1499). */
  priceLabel: number
  cta: Record<any, any> | null
  isForceUpdateRequired: boolean
  version: string
  isButtonDisabled?: boolean
}

/**
 * Orange join-offer strip under the Elite landing page subtitle.
 * Tap runs the same buy CTA path as EliteButtonV3.
 *
 * Belongs here: price-keyed offer strip image + CTA fire.
 * Does not belong here: consent UI or advisory status.
 */
export function EliteLandingJoinOfferStrip({
  priceLabel,
  cta,
  isForceUpdateRequired,
  version,
  isButtonDisabled
}: EliteLandingJoinOfferStripProps) {
  const asset = getOfferStripAsset(priceLabel)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const buyCtaExtras = {
    ...eliteBuyCtaExtras,
    amount: eliteBuyCtaExtras.amount ?? String(priceLabel)
  }

  if (!asset) return null

  const handleClick = () => {
    // Match EliteButtonV3: block when consent was explicitly unchecked
    if (!isConsentChecked()) return
    if (isForceUpdateRequired) {
      openBottomSheet(<UpgradeAppContent onDismiss={closeBottomSheet} />)
      return
    }
    if (!cta || isButtonDisabled) return

    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ...buyCtaExtras,
        eventId: version,
        buttonLabel: 'Join Offer Strip',
        screenType: version === 'ELITE_LANDING_PAGE_1L' ? version : 'MF_Elite_Landing'
      }
    })
    postCTA(JSON.stringify(cta))
  }

  return (
    <div className='mt-6 w-full px-5'>
      <button
        type='button'
        onClick={handleClick}
        disabled={isButtonDisabled || !cta}
        aria-label={asset.alt}
        className='block w-full p-0 m-0 border-0 bg-transparent cursor-pointer disabled:cursor-default disabled:opacity-100'
      >
        <img
          src={asset.src}
          alt=''
          aria-hidden='true'
          className='w-full h-auto object-contain rounded-xl'
        />
      </button>
    </div>
  )
}

function getOfferStripAsset(priceLabel: number): (typeof OFFER_STRIP_BY_PRICE)[EliteJoinOfferPriceLabel] | undefined {
  return priceLabel in OFFER_STRIP_BY_PRICE
    ? OFFER_STRIP_BY_PRICE[priceLabel as EliteJoinOfferPriceLabel]
    : undefined
}

function isConsentChecked(): boolean {
  const stored = getSessionStorageItem('consentState')
  if (!stored) return true
  try {
    const parsed = JSON.parse(stored) as {
      checked?: boolean
      topChecked?: boolean
      bottomChecked?: boolean
    }
    if ('checked' in parsed) return parsed.checked ?? false
    if ('topChecked' in parsed || 'bottomChecked' in parsed) {
      return Boolean(parsed.topChecked && parsed.bottomChecked)
    }
    return true
  } catch {
    return true
  }
}
